import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import errorMonitoringService from '../../../services/errorMonitoringService';

const RecoveryWorkflowPanel = ({ selectedError, onRefresh }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(null);

  useEffect(() => {
    if (selectedError?.id) {
      loadSuggestions();
    }
  }, [selectedError]);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      const data = await errorMonitoringService?.getRecoverySuggestions(selectedError?.id);
      setSuggestions(data || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteSuggestion = async (suggestionId) => {
    try {
      setExecuting(suggestionId);
      await errorMonitoringService?.executeRecoverySuggestion(suggestionId);
      await loadSuggestions();
      onRefresh?.();
    } catch (error) {
      console.error('Error executing suggestion:', error);
    } finally {
      setExecuting(null);
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-green-600 bg-green-50';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  if (!selectedError) {
    return (
      <div className="text-center py-12">
        <Icon name="Tool" size={48} className="mx-auto text-neutral-400 mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">No Error Selected</h3>
        <p className="text-neutral-600">Select an error from the Error Logs tab to view recovery suggestions</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selected Error Summary */}
      <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
        <h3 className="text-sm font-semibold text-neutral-900 mb-2">Selected Error</h3>
        <p className="text-sm text-neutral-700 mb-2">{selectedError?.errorMessage}</p>
        <div className="flex items-center gap-3 text-xs text-neutral-600">
          <span className="flex items-center gap-1">
            <Icon name="Package" size={14} />
            {selectedError?.affectedModule || 'Unknown'}
          </span>
          <span className="flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {selectedError?.severity}
          </span>
          <span className="flex items-center gap-1">
            <Icon name="Activity" size={14} />
            {selectedError?.recoveryStatus?.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Recovery Suggestions */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Recovery Suggestions</h3>
        
        {suggestions?.length === 0 ? (
          <div className="text-center py-8 bg-neutral-50 rounded-lg border border-neutral-200">
            <Icon name="Info" size={32} className="mx-auto text-neutral-400 mb-2" />
            <p className="text-sm text-neutral-600">No recovery suggestions available for this error</p>
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions?.map((suggestion, index) => (
              <div
                key={suggestion?.id}
                className="bg-white rounded-lg border border-neutral-200 p-4 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-neutral-900">
                        #{index + 1}
                      </span>
                      {suggestion?.isAutomated && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                          <Icon name="Zap" size={12} className="inline mr-1" />
                          Automated
                        </span>
                      )}
                      {suggestion?.confidenceScore && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${getConfidenceColor(suggestion?.confidenceScore)}`}>
                          {(suggestion?.confidenceScore * 100)?.toFixed(0)}% confidence
                        </span>
                      )}
                      {suggestion?.executed && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                          <Icon name="CheckCircle" size={12} className="inline mr-1" />
                          Executed
                        </span>
                      )}
                    </div>
                    
                    <h4 className="text-base font-semibold text-neutral-900 mb-2">
                      {suggestion?.suggestionTitle}
                    </h4>
                    
                    <p className="text-sm text-neutral-700 mb-3">
                      {suggestion?.suggestionDescription}
                    </p>

                    {suggestion?.executed && suggestion?.executionResult && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-start gap-2">
                          <Icon name="CheckCircle" size={16} className="text-green-600 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-green-900 mb-1">Execution Result</p>
                            <p className="text-xs text-green-700">{suggestion?.executionResult}</p>
                            {suggestion?.executedAt && (
                              <p className="text-xs text-green-600 mt-1">
                                Executed at: {new Date(suggestion?.executedAt)?.toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {!suggestion?.executed && (
                      <button
                        onClick={() => handleExecuteSuggestion(suggestion?.id)}
                        disabled={executing === suggestion?.id}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {executing === suggestion?.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Executing...
                          </>
                        ) : (
                          <>
                            <Icon name="Play" size={16} />
                            Execute
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recovery Status Timeline */}
      {selectedError?.recoveryAttemptedAt && (
        <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
          <h4 className="text-sm font-semibold text-neutral-900 mb-3">Recovery Timeline</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-xs">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              <span className="text-neutral-600">Recovery Attempted:</span>
              <span className="font-medium text-neutral-900">
                {new Date(selectedError?.recoveryAttemptedAt)?.toLocaleString()}
              </span>
            </div>
            {selectedError?.recoverySucceededAt && (
              <div className="flex items-center gap-3 text-xs">
                <div className="w-2 h-2 rounded-full bg-green-600"></div>
                <span className="text-neutral-600">Recovery Succeeded:</span>
                <span className="font-medium text-neutral-900">
                  {new Date(selectedError?.recoverySucceededAt)?.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecoveryWorkflowPanel;
